import * as React from "react";
import { FALLBACK_CHECKOUT, safeUserMessage } from "@/lib/user-error";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import {
  getCreditTiers,
  createCheckoutSession,
  createTopUpCheckoutSession,
  getCanBuyTopUp,
  validateReferralCode,
} from "@/lib/billing.functions";
import { getMyCredits } from "@/lib/account.functions";
import { cn } from "@/lib/utils";
import { track } from "@/lib/track";

type Status = "success" | "cancelled";

export const Route = createFileRoute("/_authed/buy-credits")({
  head: () => ({
    meta: [
      { title: "Buy Credits: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  validateSearch: (search): { status?: Status; session_id?: string } => ({
    status:
      search.status === "success" || search.status === "cancelled"
        ? search.status
        : undefined,
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
  }),
  component: BuyCreditsPage,
});

function BuyCreditsPage() {
  const { user } = useAuth();
  const search = Route.useSearch();
  const qc = useQueryClient();
  const fetchTiers = useServerFn(getCreditTiers);
  const fetchCredits = useServerFn(getMyCredits);
  const createCheckout = useServerFn(createCheckoutSession);
  const createTopUp = useServerFn(createTopUpCheckoutSession);
  const fetchCanBuyTopUp = useServerFn(getCanBuyTopUp);
  const validateReferral = useServerFn(validateReferralCode);

  const tiers = useQuery({ queryKey: ["credit-tiers"], queryFn: () => fetchTiers() });
  const canBuyTopUpQuery = useQuery({
    queryKey: ["can-buy-topup", user?.id],
    queryFn: () => fetchCanBuyTopUp(),
    enabled: !!user,
  });
  const canBuyTopUp = canBuyTopUpQuery.data?.canBuyTopUp ?? false;
  const credits = useQuery({
    queryKey: ["my-credits", user?.id],
    queryFn: () => fetchCredits(),
    enabled: !!user,
  });

  React.useEffect(() => {
    track("buy_credits_page_viewed");
  }, []);

  const startingBalanceRef = React.useRef<number | null>(null);
  const [pollTimedOut, setPollTimedOut] = React.useState(false);

  React.useEffect(() => {
    if (search.status !== "success") return;
    if (startingBalanceRef.current === null && credits.data) {
      startingBalanceRef.current = credits.data.credits_remaining;
    }
    if (!credits.data) return;
    if (
      startingBalanceRef.current !== null &&
      credits.data.credits_remaining > startingBalanceRef.current
    ) {
      return; // balance updated, stop
    }
    const startedAt = Date.now();
    const id = window.setInterval(() => {
      if (Date.now() - startedAt > 30_000) {
        setPollTimedOut(true);
        window.clearInterval(id);
        return;
      }
      qc.invalidateQueries({ queryKey: ["my-credits"] });
    }, 2000);
    return () => window.clearInterval(id);
  }, [search.status, credits.data, qc]);

  const checkout = useMutation({
    mutationFn: (tier: "starter" | "growth" | "scale") => {
      const codeToSend = appliedCode?.code ?? referralInput.trim();
      return createCheckout({
        data: {
          tier,
          ...(codeToSend ? { referralCode: codeToSend } : {}),
        },
      });
    },
    onSuccess: (res, tier) => {
      if ("referralError" in res) {
        const reason = res.referralError;
        setAppliedCode(null);
        setReferralError(
          (reason === "not_found"
            ? "That code was not found."
            : reason === "expired"
              ? "That code has expired."
              : "You have already used a referral code.") +
            " You can continue without the code.",
        );
        return;
      }
      track("checkout_started", { product: "credits", tier });
      window.location.href = res.url;
    },
  });

  const topUpCheckout = useMutation({
    mutationFn: () => createTopUp(),
    onSuccess: (res) => {
      track("checkout_started", { product: "topup" });
      window.location.href = res.url;
    },
  });

  const balanceChanged =
    startingBalanceRef.current !== null &&
    credits.data != null &&
    credits.data.credits_remaining > startingBalanceRef.current;

  const [referralInput, setReferralInput] = React.useState("");
  const [appliedCode, setAppliedCode] = React.useState<
    { code: string; bonusPct: number } | null
  >(null);
  const [referralError, setReferralError] = React.useState<string | null>(null);

  const applyReferral = useMutation({
    mutationFn: (code: string) => validateReferral({ data: { code } }),
    onSuccess: (res) => {
      if (res.valid) {
        setAppliedCode({ code: res.code, bonusPct: res.bonusPct });
        setReferralError(null);
      } else {
        setAppliedCode(null);
        setReferralError(
          res.reason === "not_found"
            ? "That code was not found."
            : res.reason === "expired"
              ? "That code has expired."
              : "You have already used a referral code.",
        );
      }
    },
    onError: () => {
      setAppliedCode(null);
      setReferralError("Could not validate that code. Try again.");
    },
  });

  return (
    <main className="mx-auto max-w-[640px] px-4 pb-16 pt-6">
      <header className="mb-6">
        <Link
          to="/dashboard"
          className="text-[14px] text-brand-muted no-underline hover:text-brand-ink"
        >
          ← Back to dashboard
        </Link>
        <h1 className="mt-2 font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
          Buy Credits.
        </h1>
        <p className="mt-1 text-[14px] text-brand-muted">
          One-time purchase. Credits never expire.
        </p>
      </header>

      {search.status === "success" && !pollTimedOut && !balanceChanged && (
        <StatusBanner tone="info">
          Payment received. Updating your balance…
        </StatusBanner>
      )}
      {search.status === "success" && balanceChanged && (
        <StatusBanner tone="success">
          Credits added — your new balance is {credits.data?.credits_remaining}.
        </StatusBanner>
      )}
      {search.status === "success" && pollTimedOut && !balanceChanged && (
        <StatusBanner tone="warn">
          Payment received but credits haven't appeared yet. Refresh the page
          in a minute, or contact support if the balance doesn't update.
        </StatusBanner>
      )}
      {search.status === "cancelled" && (
        <StatusBanner tone="warn">
          Checkout cancelled. No charge was made.
        </StatusBanner>
      )}

      <div className="mb-4 rounded-2xl border border-brand-line bg-brand-paper p-4">
        <label
          htmlFor="referral-code"
          className="block text-[14px] font-medium text-brand-ink"
        >
          Referral code (optional)
        </label>
        <div className="mt-2 flex gap-2">
          <input
            id="referral-code"
            type="text"
            value={referralInput.toUpperCase()}
            onChange={(e) => setReferralInput(e.target.value)}
            placeholder="CODENAME"
            className="flex-1 rounded-md border border-brand-line bg-white px-3 py-2 text-[14px] uppercase tracking-wider text-brand-ink placeholder:text-brand-muted/60 focus:outline-none focus:ring-1 focus:ring-brand-ink/30"
            autoComplete="off"
          />
          <Button
            type="button"
            variant="outline"
            disabled={applyReferral.isPending || !referralInput.trim()}
            onClick={() => applyReferral.mutate(referralInput.trim())}
          >
            {applyReferral.isPending ? "Checking…" : "Apply"}
          </Button>
        </div>
        {appliedCode && (
          <p className="mt-2 text-[14px] text-brand-ink">
            Code {appliedCode.code} applied: {appliedCode.bonusPct}% bonus
            credits on your purchase.{" "}
            <button
              type="button"
              onClick={() => {
                setAppliedCode(null);
                setReferralInput("");
                setReferralError(null);
              }}
              className="underline hover:text-brand-muted"
            >
              Remove
            </button>
          </p>
        )}
        {!appliedCode && referralError && (
          <p className="mt-2 text-[14px] text-destructive">{referralError}</p>
        )}
      </div>

      {canBuyTopUp && (
        <div className="rounded-2xl border border-brand-line bg-brand-paper p-5">
          <div className="flex items-baseline justify-between gap-3">
            <div>
              <h2 className="font-serif text-[22px] text-brand-ink">Top up</h2>
              <p className="mt-0.5 text-[14px] text-brand-muted">10 investors</p>
            </div>
            <div className="text-right">
              <div className="font-serif text-[28px] text-brand-ink tabular-nums">$100</div>
              <div className="text-[13px] text-brand-muted">$10.00 / investor</div>
            </div>
          </div>
          <p className="mt-3 text-[14px] text-brand-muted">
            For when you need a few more without a full package.
          </p>
          <Button
            className="mt-4 w-full"
            disabled={topUpCheckout.isPending}
            onClick={() => topUpCheckout.mutate()}
          >
            {topUpCheckout.isPending ? "Redirecting…" : "Buy →"}
          </Button>
          {topUpCheckout.error && (
            <p className="mt-2 text-[14px] text-destructive">
              {safeUserMessage(topUpCheckout.error, FALLBACK_CHECKOUT)}
            </p>
          )}
        </div>
      )}

      <section className="grid gap-3">
        {(tiers.data ?? []).map((t) => (
          <div
            key={t.tier}
            className={cn(
              "rounded-2xl border border-brand-line bg-brand-paper p-5",
              t.tier === "growth" && "border-brand-ink/40 ring-1 ring-brand-ink/10",
            )}
          >
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-serif text-[22px] text-brand-ink">{t.label}</h2>
                  {t.tier === "growth" && (
                    <span className="rounded-full bg-brand-accent/15 px-2 py-0.5 text-[14px] font-medium uppercase tracking-wider text-brand-accent">
                      Save 20%
                    </span>
                  )}
                  {t.tier === "scale" && (
                    <span className="rounded-full bg-brand-accent/15 px-2 py-0.5 text-[14px] font-medium uppercase tracking-wider text-brand-accent">
                      Save 30%
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-[14px] text-brand-muted">{t.credits.toLocaleString()} investors</p>
              </div>
              <div className="text-right">
                <div className="font-serif text-[28px] text-brand-ink tabular-nums">
                  ${t.priceUsd.toLocaleString()}
                </div>
                <div className="text-[13px] text-brand-muted">
                  ${(t.priceUsd / t.credits).toFixed(2)} / investor
                </div>
              </div>
            </div>
            <ul className="mt-3 space-y-1 text-[14px] text-brand-ink/80">
              <li className="flex items-center gap-2">
                <Check className="size-3.5 text-brand-accent" /> {t.credits.toLocaleString()} investors reached
              </li>
              {appliedCode && (
                <li className="flex items-center gap-2 text-brand-accent">
                  <Check className="size-3.5 text-brand-accent" />{" "}
                  {t.credits.toLocaleString()} +{" "}
                  {Math.floor(
                    (t.credits * appliedCode.bonusPct) / 100,
                  ).toLocaleString()}{" "}
                  bonus credits
                </li>
              )}
              <li className="flex items-center gap-2">
                <Check className="size-3.5 text-brand-accent" /> Includes 1 CapitalDart Investor Readiness Report
              </li>
              <li className="flex items-center gap-2">
                <Check className="size-3.5 text-brand-accent" /> Never expire
              </li>
            </ul>
            <Button
              className="mt-4 w-full"
              disabled={checkout.isPending}
              onClick={() => checkout.mutate(t.tier)}
            >
              {checkout.isPending && checkout.variables === t.tier
                ? "Redirecting…"
                : "Buy →"}
            </Button>
          </div>
        ))}
      </section>

      {checkout.error && (
        <p className="mt-4 text-[14px] text-destructive">
          {safeUserMessage(checkout.error, FALLBACK_CHECKOUT)}
        </p>
      )}

    </main>
  );
}

function StatusBanner({
  tone,
  children,
}: {
  tone: "success" | "warn" | "info";
  children: React.ReactNode;
}) {
  return (
    <div
      role="status"
      className={cn(
        "mb-5 rounded-xl border px-4 py-3 text-[15px]",
        tone === "success" && "border-brand-ok/30 bg-brand-ok/10 text-[oklch(0.45_0.087_155.7)]",
        tone === "warn" && "border-destructive/30 bg-destructive/10 text-destructive",
        tone === "info" && "border-brand-line bg-brand-paper text-brand-ink",
      )}
    >
      {children}
    </div>
  );
}